(function () {
  if (window.__dotdigitalEnhancedTrackingLoaded) {
    return;
  }

  window.__dotdigitalEnhancedTrackingLoaded = true;

  const STORAGE_KEYS = {
    identifiedContact: 'dotdigital_identified_contact',
    latestCartHash: 'dotdigital_latest_cart_hash',
    latestBrowseHash: 'dotdigital_latest_browse_hash'
  };

  const CART_ENDPOINT_MATCHER = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;
  const CART_POLL_DELAY_MS = 600;

  let cartPollTimer = null;

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function toPriceNumber(cents) {
    const numeric = Number(cents || 0);
    if (!Number.isFinite(numeric)) {
      return 0;
    }

    return Math.round(numeric) / 100;
  }

  function normalizeImageUrl(url) {
    if (!url || typeof url !== 'string') {
      return '';
    }

    if (url.startsWith('//')) {
      return window.location.protocol + url;
    }

    if (url.startsWith('/')) {
      return window.location.origin + url;
    }

    return url;
  }

  function normalizeUrl(url) {
    if (!url || typeof url !== 'string') {
      return window.location.href;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return window.location.origin + url;
  }

  function cleanPhone(value) {
    if (!value || typeof value !== 'string') {
      return '';
    }

    const trimmed = value.trim();

    if (!trimmed) {
      return '';
    }

    const normalized = trimmed.replace(/[^\d+]/g, '');

    if (normalized.length < 7) {
      return '';
    }

    return normalized;
  }

  function cleanEmail(value) {
    if (!value || typeof value !== 'string') {
      return '';
    }

    const trimmed = value.trim().toLowerCase();
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

    return isValid ? trimmed : '';
  }

  function getContextFromScriptTag(scriptId) {
    const script = document.getElementById(scriptId);

    if (!script || !script.textContent) {
      return null;
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function whenDotdigitalReady(callback) {
    if (window.ddg) {
      callback(window.ddg);
      return;
    }

    const onLoaded = function () {
      window.removeEventListener('ddgTagLoaded', onLoaded);
      if (window.ddg) {
        callback(window.ddg);
      }
    };

    window.addEventListener('ddgTagLoaded', onLoaded);
  }

  function safeDdgCall(methodName, payload) {
    if (!isObject(payload)) {
      return;
    }

    whenDotdigitalReady(function (ddg) {
      const method = ddg && ddg[methodName];
      if (typeof method !== 'function') {
        return;
      }

      try {
        method(payload);
      } catch (error) {
        // Keep storefront stable if ddg payload fails.
      }
    });
  }

  function cacheIdentifiedContact(contact) {
    if (!isObject(contact)) {
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEYS.identifiedContact, JSON.stringify(contact));
    } catch (error) {
      // Storage can fail in private mode; ignore.
    }
  }

  function getCachedContact() {
    try {
      const cached = localStorage.getItem(STORAGE_KEYS.identifiedContact);
      if (!cached) {
        return null;
      }
      const parsed = JSON.parse(cached);
      return isObject(parsed) ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function identifyContact(rawContact) {
    if (!isObject(rawContact)) {
      return;
    }

    const email = cleanEmail(rawContact.email);
    const mobileNumber = cleanPhone(rawContact.mobileNumber || rawContact.phone);
    const firstName = (rawContact.firstName || '').trim();
    const lastName = (rawContact.lastName || '').trim();

    if (!email && !mobileNumber) {
      return;
    }

    const payload = {
      email: email || undefined,
      mobileNumber: mobileNumber || undefined,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      fullName: [firstName, lastName].filter(Boolean).join(' ') || undefined
    };

    safeDdgCall('identify', payload);
    cacheIdentifiedContact(payload);
  }

  function identifyFromDomEvent(event) {
    const target = event && event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if (target.type !== 'email' && target.type !== 'tel') {
      return;
    }

    const form = target.form || target.closest('form') || document;
    const emailInput = form.querySelector('input[type="email"], input[name*="email" i]');
    const phoneInput = form.querySelector('input[type="tel"], input[name*="phone" i], input[name*="mobile" i]');
    const firstNameInput = form.querySelector('input[name*="first" i]');
    const lastNameInput = form.querySelector('input[name*="last" i]');

    identifyContact({
      email: emailInput ? emailInput.value : '',
      mobileNumber: phoneInput ? phoneInput.value : '',
      firstName: firstNameInput ? firstNameInput.value : '',
      lastName: lastNameInput ? lastNameInput.value : '',
    });
  }

  function mapCartItemToDdgProduct(item) {
    const quantity = Number(item.quantity || 0);
    const unitPrice = toPriceNumber(item.original_price);
    const salePrice = toPriceNumber(item.final_price || item.price);
    const totalPrice = toPriceNumber(item.final_line_price || item.line_price);
    const productUrl = normalizeUrl(item.url || '');
    const imageUrl = normalizeImageUrl(
      item.image ||
      (item.featured_image && item.featured_image.url) ||
      ''
    );

    return {
      productId: String(item.product_id || ''),
      sku: item.sku || String(item.variant_id || item.id || ''),
      name: item.product_title || item.title || '',
      description: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
      category: item.product_type || '',
      unitPrice: unitPrice,
      salePrice: salePrice || unitPrice,
      quantity: quantity,
      totalPrice: totalPrice || salePrice * quantity,
      imageUrl: imageUrl,
      productUrl: productUrl
    };
  }

  function createCartPayload(cart) {
    if (!isObject(cart) || !Array.isArray(cart.items) || cart.items.length === 0) {
      return null;
    }

    const products = cart.items.map(mapCartItemToDdgProduct).filter(function (product) {
      return product.sku && product.name;
    });

    if (products.length === 0) {
      return null;
    }

    const shipping = 0;
    const taxAmount = 0;
    const subtotal = toPriceNumber(cart.items_subtotal_price);
    const discountAmount = toPriceNumber(cart.total_discount || 0);
    const grandTotal = toPriceNumber(cart.total_price);

    return {
      cartId: String(cart.token || ''),
      cartPhase: 'ORDER_PENDING',
      currency: cart.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '',
      subtotal: subtotal,
      shipping: shipping,
      discountAmount: discountAmount,
      taxAmount: taxAmount,
      grandTotal: grandTotal || subtotal,
      cartUrl: window.location.origin + '/cart',
      products: products
    };
  }

  function cartPayloadHash(payload) {
    try {
      return JSON.stringify(payload);
    } catch (error) {
      return '';
    }
  }

  function getStoredCartHash() {
    try {
      return localStorage.getItem(STORAGE_KEYS.latestCartHash) || '';
    } catch (error) {
      return '';
    }
  }

  function storeCartHash(hash) {
    if (!hash) {
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEYS.latestCartHash, hash);
    } catch (error) {
      // Ignore storage errors.
    }
  }

  function getStoredBrowseHash() {
    try {
      return sessionStorage.getItem(STORAGE_KEYS.latestBrowseHash) || '';
    } catch (error) {
      return '';
    }
  }

  function storeBrowseHash(hash) {
    if (!hash) {
      return;
    }

    try {
      sessionStorage.setItem(STORAGE_KEYS.latestBrowseHash, hash);
    } catch (error) {
      // Ignore storage errors.
    }
  }

  function sendCartUpdate(cart) {
    const payload = createCartPayload(cart);
    if (!payload) {
      return;
    }

    const nextHash = cartPayloadHash(payload);
    const previousHash = getStoredCartHash();
    if (nextHash && nextHash === previousHash) {
      return;
    }

    safeDdgCall('cartUpdate', payload);
    storeCartHash(nextHash);
  }

  function fetchAndSendCartUpdate() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) {
          return null;
        }
        return response.json();
      })
      .then(function (cart) {
        if (!cart || !cart.item_count) {
          return;
        }
        sendCartUpdate(cart);
      })
      .catch(function () {
        // Ignore cart fetch failures; retry will happen on next interaction.
      });
  }

  function queueCartSync() {
    if (cartPollTimer) {
      window.clearTimeout(cartPollTimer);
    }

    cartPollTimer = window.setTimeout(function () {
      fetchAndSendCartUpdate();
    }, CART_POLL_DELAY_MS);
  }

  function patchFetchForCartChanges() {
    if (typeof window.fetch !== 'function') {
      return;
    }

    const originalFetch = window.fetch.bind(window);

    window.fetch = function (input, init) {
      const requestUrl = typeof input === 'string' ? input : (input && input.url ? input.url : '');
      const shouldObserve = CART_ENDPOINT_MATCHER.test(requestUrl || '');

      return originalFetch(input, init).then(function (response) {
        if (shouldObserve) {
          queueCartSync();
        }
        return response;
      });
    };
  }

  function trackProductBrowse() {
    const productContext = getContextFromScriptTag('DotdigitalProductData');
    if (!isObject(productContext)) {
      return;
    }

    const normalizedVariants = (Array.isArray(productContext.variants) ? productContext.variants : []).map(function (variant) {
      return {
        id: String((variant && variant.id) || ''),
        name: (variant && variant.name) || '',
        stock: Number((variant && variant.stock) || 0),
        url: normalizeUrl((variant && variant.url) || ''),
        image: normalizeImageUrl((variant && variant.image) || '')
      };
    }).filter(function (variant) {
      return variant.id;
    });

    const payload = {
      productId: String(productContext.productId || ''),
      sku: productContext.sku || '',
      name: productContext.name || '',
      url: normalizeUrl(productContext.url || window.location.pathname),
      imageUrl: normalizeImageUrl(productContext.imageUrl || ''),
      imageThumbnailUrl: normalizeImageUrl(productContext.imageThumbnailUrl || productContext.imageUrl || ''),
      price: Number(productContext.price || 0),
      salePrice: Number(productContext.salePrice || productContext.price || 0),
      currency: productContext.currency || '',
      description: productContext.description || '',
      categories: Array.isArray(productContext.categories) ? productContext.categories : [],
      brand: productContext.brand || '',
      variantId: productContext.variantId ? String(productContext.variantId) : undefined,
      variants: normalizedVariants
    };

    const hasValidPrice = Number.isFinite(payload.price);

    if (!payload.productId || !payload.sku || !payload.name || !payload.url || !payload.imageUrl || !payload.currency || !hasValidPrice) {
      return;
    }

    const browseHash = [payload.productId, payload.variantId || '', payload.url].join('|');
    const previousBrowseHash = getStoredBrowseHash();
    if (browseHash && browseHash === previousBrowseHash) {
      return;
    }

    safeDdgCall('productBrowse', payload);
    storeBrowseHash(browseHash);
  }

  function setupIdentifyListeners() {
    document.addEventListener('change', identifyFromDomEvent, true);
    document.addEventListener('blur', identifyFromDomEvent, true);
    document.addEventListener('submit', function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const emailInput = form.querySelector('input[type="email"], input[name*="email" i]');
      const phoneInput = form.querySelector('input[type="tel"], input[name*="phone" i], input[name*="mobile" i]');

      identifyContact({
        email: emailInput ? emailInput.value : '',
        mobileNumber: phoneInput ? phoneInput.value : ''
      });
    }, true);
  }

  function initIdentifyFromThemeContext() {
    const customerContext = getContextFromScriptTag('DotdigitalCustomerData');
    if (isObject(customerContext)) {
      identifyContact(customerContext);
      return;
    }

    const cachedContact = getCachedContact();
    if (cachedContact) {
      identifyContact(cachedContact);
    }
  }

  function init() {
    initIdentifyFromThemeContext();
    setupIdentifyListeners();
    trackProductBrowse();
    fetchAndSendCartUpdate();
    patchFetchForCartChanges();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
